import { motion } from 'framer-motion';
import PageTransition from '../components/PageTransition';
import { news } from './News';

export default function Home() {
  return (
    <PageTransition>
      <div className="bg-white">
        {/* Hero Section with animation */}
        <motion.div 
          className="relative isolate px-6 pt-10 lg:px-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="mx-auto max-w-2xl py-10 sm:py-12 lg:py-10">
            <div className="text-center">
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                清华大学陈文颖课题组
                <br />
                <span className="mt-2 block"></span>
              </h1>
              <p className="mt-4 text-2xl leading-8 text-gray-600">
                能源系统分析团队
              </p>
              <div className="mt-8 flex items-center justify-center gap-x-6">
                <a
                  href="/publications"
                  className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  研究成果
                </a>
                <a href="/news" className="text-sm font-semibold leading-6 text-gray-900">
                  最新动态 <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Research Areas Section with stagger effect */}
        <div className="bg-white py-16 sm:py-24">
          <motion.div 
            className="mx-auto max-w-7xl px-6 lg:px-8"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="mx-auto max-w-2xl lg:text-center">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                研究方向
              </h2>
              <p className="mt-4 text-lg leading-8 text-gray-600">
                我们的研究涵盖以下主要领域
              </p>
            </div>
            <div className="mx-auto mt-12 max-w-2xl sm:mt-16 lg:mt-16 lg:max-w-none">
              <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-12 lg:max-w-none lg:grid-cols-3">
                <div className="flex flex-col">
                  <dt className="text-lg font-semibold leading-7 text-gray-900">
                    能源系统优化与建模
                  </dt>
                  <dd className="mt-3 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">
                      基于 TIMES 模型框架开发中国能源系统优化模型，研究能源系统低碳转型路径。重点关注电力系统脱碳、跨省电力传输、氢能发展等关键议题，为实现碳中和目标提供定量分析支撑。
                    </p>
                  </dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-lg font-semibold leading-7 text-gray-900">
                    碳中和战略与政策
                  </dt>
                  <dd className="mt-3 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">
                      研究中国碳中和目标下的减排路径和政策措施，评估各省区市碳中和路径差异。重点分析 CCUS 技术布局、区域协同减排机制等，为国家和地方碳中和战略提供科学依据。
                    </p>
                  </dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-lg font-semibold leading-7 text-gray-900">
                    能源环境经济分析
                  </dt>
                  <dd className="mt-3 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">
                      开展能源-环境-经济系统集成分析，研究低碳转型的社会经济影响。重点关注技术创新路径、水资源约束、能源贸易等多维度问题，探索可持续发展的综合解决方案。
                    </p>
                  </dd>
                </div>
              </dl>
            </div>
          </motion.div>
        </div>

        {/* Latest News Section with slide-in effect */}
        <motion.div 
          className="bg-gray-50 py-16 sm:py-24"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl lg:mx-0">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                最新动态
              </h2>
            </div>
            <div className="mx-auto mt-8 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-12 border-t border-gray-200 pt-8 sm:mt-12 sm:pt-12 lg:mx-0 lg:max-w-none lg:grid-cols-3">
              {news.map((item) => (
                <article key={item.title} className="flex max-w-xl flex-col items-start justify-between">
                  <div className="group relative">
                    <h3 className="mt-3 text-lg font-semibold leading-6 text-gray-900">
                      {item.title}
                    </h3>
                    <p className="mt-4 line-clamp-3 text-sm leading-6 text-gray-600">
                      {item.content}
                    </p>
                  </div>
                  <div className="relative mt-4 flex items-center gap-x-4">
                    <time dateTime={item.date} className="text-sm leading-6 text-gray-600">
                      {item.date}
                    </time>
                  </div>
                  {item.link && (
                    <div className="mt-4">
                      <a 
                        href={item.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-indigo-600 hover:text-indigo-500"
                      >
                        阅读更多 →
                      </a>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </PageTransition>
  );
}